/**
 * FirstRunFlow.jsx — Phase 2 of the onboarding system (Jun 2026).
 *
 * The First Run: a real, teacher-driven lesson generation narrated by a
 * non-blocking floating panel. The teacher finishes holding a lesson
 * they keep. Runs ONCE for every signed-in user (after the profile gate
 * is satisfied), dismissible at every step, replayable from the help
 * affordance ("Show me how this works" in the user menu).
 *
 * It consumes the Phase-1 flag mechanism (users.onboarding_seen.first_run,
 * versioned) — it is NOT a second onboarding mechanism. All copy is
 * verbatim from lib/onboardingContent.js (first_run).
 *
 * ALLOW-LIST (build constraint 1): every option offered is read at
 * runtime from the shared curriculum + skills sources — no hardcoded
 * grade/subject/skill/expectation default anywhere in this file:
 *   · grades/subjects/expectations → useCurriculum() (/api/curriculum,
 *     Ontario Grades 4–8) via getGradesFromCurriculum /
 *     getSubjectsForGrade / getSpecificsForGradeSubject
 *   · skills → SKILL_FOCUS_OPTIONS (lib/options.js) — the same set the
 *     free generator's ALLOWED_SKILLS mirrors
 *   · short name → applyShortNames (lib/skillNames.js)
 * Free users therefore can never be offered something the restricted
 * free generator would refuse.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, ChevronLeft, ChevronRight, Check, ArrowRight } from "lucide-react";

import { useAuth } from "../../contexts/AuthContext";
import {
  useCurriculum,
  getGradesFromCurriculum,
  getSubjectsForGrade,
  getSpecificsForGradeSubject,
} from "../../lib/curriculum";
import { SKILL_FOCUS_OPTIONS } from "../../lib/options";
import { applyShortNames } from "../../lib/skillNames";
import { generateActivity, listActivities } from "../../lib/api";
import { postAuthenticatedFreeLesson } from "../../lib/freeLessonApi";
import { updateTeacherProfile } from "../../lib/auth";
import { markOnboardingSeen, logFirstRunBailout } from "../../lib/onboarding";
import { getOnboardingSurface } from "../../lib/onboardingContent";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui/select";
import { AtomLoader, GENERATOR_LOADER_MESSAGES } from "../AtomLoader";

const MINT = "#3EB489";
const SURFACE = "first_run";

const isPaidUser = (u) => !!(u?.subscription?.is_active || u?.is_admin);

// Best-effort spotlight — scroll a matched element into view + ring it.
// Never throws, never blocks; if nothing matches, the panel copy alone
// carries the step.
const _clearSpotlight = () => {
  document.querySelectorAll("[data-firstrun-spot]").forEach((el) => {
    el.style.outline = "";
    el.style.outlineOffset = "";
    el.style.borderRadius = "";
    el.removeAttribute("data-firstrun-spot");
  });
};
const spotlight = (finder) => {
  try {
    _clearSpotlight();
    const el = finder();
    if (!el) return;
    el.setAttribute("data-firstrun-spot", "1");
    el.style.outline = `3px solid ${MINT}`;
    el.style.outlineOffset = "3px";
    el.style.borderRadius = "8px";
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  } catch {
    /* best-effort */
  }
};
const _findByText = (needle) => {
  if (!needle) return null;
  const n = needle.toLowerCase();
  // Exclude the onboarding panel itself so we spotlight the LESSON, not
  // the call-out copy (which also contains the skill name).
  const panel = document.querySelector('[data-testid="first-run-flow"]');
  const nodes = document.querySelectorAll(
    "h1,h2,h3,h4,p,li,td,strong,em,span",
  );
  for (const el of nodes) {
    if (panel && panel.contains(el)) continue;
    if ((el.textContent || "").toLowerCase().includes(n)) return el;
  }
  return null;
};

export default function FirstRunFlow({ profileGateActive, replayNonce = 0 }) {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const { data: curriculum } = useCurriculum();
  const surface = getOnboardingSurface(SURFACE);
  const version = surface?.version || null;
  const C = surface?.copy || {};

  const storedVersion = user?.onboarding_seen?.[SURFACE];
  const seen = !!surface && storedVersion === version;

  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("step1"); // step1|step2|step3|step4|generating|callout|ending
  const [calloutIndex, setCalloutIndex] = useState(0);

  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [skill, setSkill] = useState("");
  const [exp, setExp] = useState(null); // {code, description, strand}
  const [priorCount, setPriorCount] = useState(null);

  const autoOpenedRef = useRef(false);
  const dismissedRef = useRef(false);
  const lastReplayRef = useRef(0);
  const abortedRef = useRef(false);
  const genTimerRef = useRef(null);

  const paid = isPaidUser(user);
  const free = !!user && !paid;

  const storedGrades = useMemo(() => user?.grades_taught || [], [user]);
  const storedSubjects = useMemo(() => user?.subjects_taught || [], [user]);

  // Step-1 case (A empty / B single+single / C multiple).
  const step1Case =
    storedGrades.length === 0 && storedSubjects.length === 0
      ? "A"
      : storedGrades.length === 1 && storedSubjects.length === 1
      ? "B"
      : "C";

  // ---- gate + auto-open ----
  const shouldShow = !!surface && !!user && !profileGateActive && !seen;
  useEffect(() => {
    if (shouldShow && !autoOpenedRef.current && !dismissedRef.current) {
      autoOpenedRef.current = true;
      setOpen(true);
    }
  }, [shouldShow]);

  // ---- replay from the help affordance ----
  useEffect(() => {
    if (replayNonce > 0 && replayNonce !== lastReplayRef.current) {
      lastReplayRef.current = replayNonce;
      dismissedRef.current = false;
      resetFlow();
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayNonce]);

  // ---- initial data when the panel opens ----
  const resetFlow = useCallback(() => {
    setCalloutIndex(0);
    // Skill pre-selected from the allow-list (runtime, never hardcoded).
    setSkill(SKILL_FOCUS_OPTIONS[0] || "");
    setExp(null);
    if (step1Case === "B") {
      setGrade(storedGrades[0]);
      setSubject(storedSubjects[0]);
      setPhase("step2");
    } else if (step1Case === "C") {
      setGrade(storedGrades[0] || "");
      setSubject("");
      setPhase("step1");
    } else {
      setGrade("");
      setSubject("");
      setPhase("step1");
    }
  }, [step1Case, storedGrades, storedSubjects]);

  useEffect(() => {
    if (open) resetFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Prior-activity count → "existing user" (paid only; free is always 6c).
  useEffect(() => {
    if (!open || !paid || priorCount !== null) return;
    listActivities()
      .then((rows) => setPriorCount(Array.isArray(rows) ? rows.length : 0))
      .catch(() => setPriorCount(0));
  }, [open, paid, priorCount]);

  // ---- option lists (allow-list, runtime) ----
  const gradeOptions = useMemo(() => {
    if (step1Case === "C") {
      const curr = getGradesFromCurriculum(curriculum);
      return storedGrades.filter((g) => curr.includes(g));
    }
    return getGradesFromCurriculum(curriculum);
  }, [step1Case, curriculum, storedGrades]);

  const subjectOptions = useMemo(() => {
    if (!grade) return [];
    const currSubjects = getSubjectsForGrade(curriculum, grade);
    if (step1Case === "C") return storedSubjects.filter((s) => currSubjects.includes(s));
    return currSubjects;
  }, [grade, curriculum, step1Case, storedSubjects]);

  const expectations = useMemo(() => {
    if (!grade || !subject) return [];
    return (getSpecificsForGradeSubject(curriculum, grade, subject) || []).slice(0, 3);
  }, [curriculum, grade, subject]);

  const skillShort = applyShortNames(skill);
  // Copy says "Grade {grade}" verbatim, so {grade} interpolates to the
  // number only ("Grade 4" → "4"); logic/payloads keep the full grade.
  const gradeNum = (grade || "").replace(/^Grade\s+/i, "");
  const fill = useCallback(
    (tpl) =>
      (tpl || "")
        .replaceAll("{grade}", gradeNum)
        .replaceAll("{subject}", subject)
        .replaceAll("{skill_name}", skill)
        .replaceAll("{skill_short_name}", skillShort),
    [gradeNum, subject, skill, skillShort],
  );

  const endingType = free ? "free" : priorCount > 0 ? "existing" : "new_trial";

  // ---- flag write (completion OR dismissal). Per-surface only. ----
  const persistSeen = useCallback(async () => {
    if (!version) return;
    setUser?.((u) =>
      u ? { ...u, onboarding_seen: { ...(u.onboarding_seen || {}), [SURFACE]: version } } : u,
    );
    try {
      const map = await markOnboardingSeen(SURFACE, version);
      setUser?.((u) => (u ? { ...u, onboarding_seen: map } : u));
    } catch {
      /* optimistic flag still hides it this session */
    }
  }, [version, setUser]);

  const dismiss = useCallback(() => {
    dismissedRef.current = true;
    _clearSpotlight();
    setOpen(false);
    persistSeen();
  }, [persistSeen]);

  // ---- generation ----
  const runGenerate = useCallback(async () => {
    setPhase("generating");
    abortedRef.current = false;
    const _startedAt = Date.now();
    // Safety bail-out: if the generator stalls (e.g. an internal
    // regenerate loop), don't strand the teacher on the loader forever.
    // Reverts to step 3 with a gentle toast. The 3 loader lines still
    // loop with no duration promise until this fires. Every bail-out is
    // logged so we can see the real failure rate.
    if (genTimerRef.current) clearTimeout(genTimerRef.current);
    genTimerRef.current = setTimeout(() => {
      if (dismissedRef.current) return;
      abortedRef.current = true;
      logFirstRunBailout({
        reason: "generation_timeout",
        grade,
        subject,
        skill,
        expectation_code: exp?.code || "",
        tier: free ? "free" : "paid",
        elapsed_ms: Date.now() - _startedAt,
      });
      toast("This one's taking longer than usual — let's try that again.");
      setPhase("step3");
    }, 240000);
    const _finishTimer = () => {
      if (genTimerRef.current) clearTimeout(genTimerRef.current);
      genTimerRef.current = null;
    };
    try {
      if (free) {
        const res = await postAuthenticatedFreeLesson({
          grade,
          subject,
          transferable_skill: skill,
          context: "",
          expectation_code: exp?.code || "",
          expectation_text: exp?.description || "",
          include_worksheet: true,
          include_exit_ticket: true,
        });
        if (!res?.token) throw new Error("no_token");
        try {
          window.sessionStorage.setItem(
            `free_lesson_session:${res.token}`,
            JSON.stringify({
              lesson: res.lesson,
              grade,
              subject,
              transferable_skill: skill,
              context: "",
              expectation_code: exp?.code || "",
              expectation_text: exp?.description || "",
              include_worksheet: true,
              include_exit_ticket: true,
              generated_at: new Date().toISOString(),
            }),
          );
        } catch {
          /* private mode — render page has its own fallback */
        }
        _finishTimer();
        if (dismissedRef.current || abortedRef.current) return;
        navigate(`/free-lesson/${encodeURIComponent(res.token)}`);
      } else {
        const activity = await generateActivity({
          skill_focus: skill,
          grade,
          subject,
          expectation_code: exp?.code || "",
          expectation_text: exp?.description || "",
          expectations_detail: exp
            ? [{ code: exp.code, description: exp.description, strand: exp.strand || "" }]
            : [],
          prompt: "",
          activity_type: "Lesson",
          output_type: "lesson",
          include_worksheet: true,
          include_exit_ticket: true,
        });
        if (!activity?.id) throw new Error("no_activity");
        _finishTimer();
        if (dismissedRef.current || abortedRef.current) return;
        navigate(`/lesson/${encodeURIComponent(activity.id)}`);
      }
      setPhase("callout");
      setCalloutIndex(0);
    } catch (err) {
      _finishTimer();
      if (abortedRef.current || dismissedRef.current) return;
      const detail = err?.response?.data?.detail;
      logFirstRunBailout({
        reason: err?.response?.status === 429 ? "monthly_cap" : "generation_error",
        grade,
        subject,
        skill,
        expectation_code: exp?.code || "",
        tier: free ? "free" : "paid",
        elapsed_ms: Date.now() - _startedAt,
      });
      if (err?.response?.status === 429) {
        toast.error("You've used your free lessons for this month.");
      } else {
        toast.error(
          typeof detail === "string"
            ? detail
            : "We couldn't build your lesson this time. Please try again.",
        );
      }
      setPhase("step3");
    }
  }, [free, grade, subject, skill, exp, navigate]);

  // ---- step-1 advance (writes profile in Case A) ----
  const startBuilding = useCallback(async () => {
    if (step1Case === "A") {
      try {
        const res = await updateTeacherProfile({
          grades_taught: [grade],
          subjects_taught: [subject],
          privacy_consent: true,
        });
        setUser?.((u) =>
          u
            ? {
                ...u,
                grades_taught: res?.grades_taught || [grade],
                subjects_taught: res?.subjects_taught || [subject],
                profile_complete: true,
              }
            : u,
        );
      } catch {
        /* non-fatal — proceed with the in-flow selection */
      }
    }
    setPhase("step2");
  }, [step1Case, grade, subject, setUser]);

  // ---- spotlight per callout ----
  useEffect(() => {
    if (phase !== "callout") return undefined;
    const t = setTimeout(() => {
      if (calloutIndex === 0) spotlight(() => _findByText(skillShort));
      else if (calloutIndex === 1) spotlight(() => _findByText(exp?.code || ""));
      else
        spotlight(
          () =>
            document.querySelector('[data-testid^="export-"]') ||
            document.querySelector('[data-testid="free-lesson-save-btn"]'),
        );
    }, 900);
    return () => clearTimeout(t);
  }, [phase, calloutIndex, skillShort, exp]);

  useEffect(
    () => () => {
      _clearSpotlight();
      if (genTimerRef.current) clearTimeout(genTimerRef.current);
    },
    [],
  );

  if (!open || !surface) return null;

  // ---------- render helpers ----------
  const Panel = ({ children, footer }) => (
    <div
      data-testid="first-run-flow"
      role="dialog"
      aria-label="First run"
      className="fixed z-40 inset-x-0 bottom-0 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[400px] px-3 pb-3 sm:px-0 sm:pb-0 pointer-events-none"
    >
      <div className="pointer-events-auto relative w-full max-h-[85vh] overflow-y-auto rounded-2xl border border-[#4a6984]/15 bg-white shadow-2xl">
        <div className="h-1 w-full" style={{ backgroundColor: MINT }} />
        <button
          type="button"
          onClick={dismiss}
          data-testid="first-run-close"
          aria-label="Dismiss"
          className="absolute right-2 top-2.5 h-8 w-8 grid place-items-center rounded-full text-[#4a6984] hover:bg-[#f4f5f7] transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="p-5 pt-4">{children}</div>
        {footer && (
          <div className="px-5 pb-5 -mt-1 flex items-center justify-end gap-2">{footer}</div>
        )}
      </div>
    </div>
  );

  const Headline = ({ text }) => (
    <p className="whitespace-pre-line font-extrabold text-lg leading-snug tracking-tight text-[#1c2d3d]">
      {text}
    </p>
  );

  const PrimaryBtn = ({ children, onClick, disabled, testid }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="inline-flex items-center gap-1 rounded-md px-4 py-2 text-xs font-extrabold text-white transition-colors disabled:opacity-40"
      style={{ backgroundColor: MINT }}
    >
      {children}
    </button>
  );

  // ---------- STEP 1 ----------
  if (phase === "step1") {
    return (
      <Panel
        footer={
          <PrimaryBtn
            testid="first-run-start-building"
            onClick={startBuilding}
            disabled={!grade || !subject}
          >
            {C.step1_button} <ArrowRight className="h-4 w-4" />
          </PrimaryBtn>
        }
      >
        <Headline text={C.step1_headline} />
        <p className="mt-3 text-sm font-semibold text-[#1c2d3d]">
          {step1Case === "C" ? C.step1_question_c : C.step1_question_a}
        </p>
        <div className="mt-3 space-y-2">
          <Select
            value={grade}
            onValueChange={(g) => {
              setGrade(g);
              setSubject("");
            }}
          >
            <SelectTrigger data-testid="first-run-grade" className="w-full">
              <SelectValue placeholder="Grade" />
            </SelectTrigger>
            <SelectContent>
              {gradeOptions.map((g) => (
                <SelectItem key={g} value={g}>
                  {g}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={subject} onValueChange={setSubject} disabled={!grade}>
            <SelectTrigger data-testid="first-run-subject" className="w-full">
              <SelectValue placeholder="Subject" />
            </SelectTrigger>
            <SelectContent>
              {subjectOptions.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {step1Case === "C" && (
          <p className="mt-2 text-[11px] text-[#4a6984]/75">{C.step1_note_c}</p>
        )}
      </Panel>
    );
  }

  // ---------- STEP 2 (skill) ----------
  if (phase === "step2") {
    return (
      <Panel
        footer={
          <>
            <button
              type="button"
              onClick={() => {
                if (step1Case !== "B") setPhase("step1");
              }}
              data-testid="first-run-step2-back"
              disabled={step1Case === "B"}
              className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-xs font-bold text-[#4a6984] hover:bg-[#f4f5f7] disabled:opacity-30"
            >
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
            <PrimaryBtn testid="first-run-step2-next" onClick={() => setPhase("step3")}>
              {C.step2_button} <ChevronRight className="h-4 w-4" />
            </PrimaryBtn>
          </>
        }
      >
        <Headline text={fill(C.step2)} />
        <div className="mt-3">
          <Select value={skill} onValueChange={setSkill}>
            <SelectTrigger data-testid="first-run-skill" className="w-full">
              <SelectValue placeholder="Skill" />
            </SelectTrigger>
            <SelectContent>
              {SKILL_FOCUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Panel>
    );
  }

  // ---------- STEP 3 (expectation) ----------
  if (phase === "step3") {
    return (
      <Panel
        footer={
          <>
            <button
              type="button"
              onClick={() => setPhase("step2")}
              data-testid="first-run-step3-back"
              className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-xs font-bold text-[#4a6984] hover:bg-[#f4f5f7]"
            >
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
            <PrimaryBtn
              testid="first-run-step3-next"
              onClick={() => setPhase("step4")}
              disabled={!exp}
            >
              {C.step3_button} <ChevronRight className="h-4 w-4" />
            </PrimaryBtn>
          </>
        }
      >
        <Headline text={fill(C.step3)} />
        <div className="mt-3 space-y-2">
          {expectations.map((e) => {
            const active = exp?.code === e.code;
            return (
              <button
                key={e.code}
                type="button"
                onClick={() => setExp(e)}
                data-testid={`first-run-expectation-${e.code}`}
                className={`w-full text-left rounded-lg border p-3 transition-colors ${
                  active
                    ? "border-[#1c2d3d]/40 bg-[#96ded1]/20"
                    : "border-[#4a6984]/20 hover:bg-[#f4f5f7]"
                }`}
              >
                <span className="block text-[11px] font-extrabold uppercase tracking-wide text-[#4a6984]">
                  {e.code}
                </span>
                <span className="block mt-1 text-xs text-[#1c2d3d] leading-snug">
                  {e.description}
                </span>
              </button>
            );
          })}
          {expectations.length === 0 && (
            <p className="text-xs text-[#4a6984]/70 italic">
              Loading curriculum…
            </p>
          )}
        </div>
        <p className="mt-2 text-[11px] text-[#4a6984]/70">{C.step3_link}</p>
      </Panel>
    );
  }

  // ---------- STEP 4 (generate trigger + looping loader) ----------
  if (phase === "step4") {
    return (
      <Panel
        footer={
          <PrimaryBtn testid="first-run-build" onClick={runGenerate}>
            {C.step4_button} <ArrowRight className="h-4 w-4" />
          </PrimaryBtn>
        }
      >
        <Headline text={fill(C.step3)} />
        <p className="mt-2 text-xs text-[#4a6984]/80">
          {exp?.code} — {exp?.description}
        </p>
      </Panel>
    );
  }

  if (phase === "generating") {
    return (
      <Panel>
        <div className="flex flex-col items-center py-2" data-testid="first-run-loader">
          <AtomLoader
            size="sm"
            messages={GENERATOR_LOADER_MESSAGES}
          />
        </div>
      </Panel>
    );
  }

  // ---------- STEP 5 (three call-outs) ----------
  if (phase === "callout") {
    const bodies = [fill(C.callout_a), C.callout_b, C.callout_c];
    const isLast = calloutIndex >= 2;
    return (
      <Panel
        footer={
          <>
            {calloutIndex > 0 && (
              <button
                type="button"
                onClick={() => setCalloutIndex((i) => Math.max(0, i - 1))}
                data-testid="first-run-callout-back"
                className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-xs font-bold text-[#4a6984] hover:bg-[#f4f5f7]"
              >
                <ChevronLeft className="h-4 w-4" /> Back
              </button>
            )}
            <PrimaryBtn
              testid={isLast ? "first-run-callout-done" : "first-run-callout-next"}
              onClick={() => (isLast ? setPhase("ending") : setCalloutIndex((i) => i + 1))}
            >
              {isLast ? (
                <>
                  Next <ChevronRight className="h-4 w-4" />
                </>
              ) : (
                <>
                  Next <ChevronRight className="h-4 w-4" />
                </>
              )}
            </PrimaryBtn>
          </>
        }
      >
        <Headline text={bodies[calloutIndex]} />
        <div className="mt-3 flex items-center gap-1.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: i === calloutIndex ? 18 : 6,
                backgroundColor: i === calloutIndex ? MINT : "rgba(74,105,132,0.25)",
              }}
            />
          ))}
        </div>
      </Panel>
    );
  }

  // ---------- STEP 6 (endings) ----------
  if (phase === "ending") {
    if (endingType === "existing") {
      return (
        <Panel
          footer={
            <PrimaryBtn testid="first-run-ending-done" onClick={dismiss}>
              {C.ending_existing_button} <Check className="h-4 w-4" />
            </PrimaryBtn>
          }
        >
          <Headline text={C.ending_existing} />
        </Panel>
      );
    }
    if (endingType === "free") {
      return (
        <Panel>
          <Headline text={C.ending_free} />
          <div className="mt-4 space-y-2">
            <PrimaryBtn
              testid="first-run-ending-save"
              onClick={() => {
                document.querySelector('[data-testid="free-lesson-save-btn"]')?.click();
                dismiss();
              }}
            >
              {C.ending_free_primary} <Check className="h-4 w-4" />
            </PrimaryBtn>
            <button
              type="button"
              data-testid="first-run-ending-trial"
              onClick={() => {
                dismiss();
                navigate("/pricing");
              }}
              className="block text-left text-[11px] text-[#4a6984]/80 hover:text-[#1c2d3d] underline decoration-dotted underline-offset-2"
            >
              {C.ending_free_secondary}
            </button>
          </div>
        </Panel>
      );
    }
    // new_trial
    return (
      <Panel>
        <Headline text={C.ending_new_trial} />
        <div className="mt-4 space-y-2">
          <PrimaryBtn
            testid="first-run-ending-build-another"
            onClick={() => {
              dismiss();
              navigate(
                `/generator?grade=${encodeURIComponent(grade)}&subject=${encodeURIComponent(subject)}`,
              );
            }}
          >
            {C.ending_new_trial_primary} <ArrowRight className="h-4 w-4" />
          </PrimaryBtn>
          <button
            type="button"
            data-testid="first-run-ending-look-around"
            onClick={dismiss}
            className="block text-left text-[11px] text-[#4a6984]/80 hover:text-[#1c2d3d] underline decoration-dotted underline-offset-2"
          >
            {C.ending_new_trial_secondary}
          </button>
        </div>
      </Panel>
    );
  }

  return null;
}
